import React from "react";
import { View, Text, StyleSheet, Image, TouchableOpacity } from "react-native";
import { useTheme } from "../../../context/ThemeContext";
import { useDesign } from "../../../context/DesignContext";
import { Ionicons, FontAwesome6 } from "@expo/vector-icons";
import { withComponentEditor } from "../../BaseComponent";
import { useProfilePage } from "../ProfilePage";
import { useContent } from "../../../context/ContentContext";
const componentType = "profileHeader";
const ProfileHeader = ({ theme, pageContext, onComponentSelect }) => {
	const { activeTab, handleTabPress } = pageContext;
	const { fetchDesign } = useDesign();
	const { fetchContent } = useContent();
	const design = fetchDesign(componentType);
	const content = fetchContent(componentType);

	const handlePress = () => {
		if (onComponentSelect) {
			onComponentSelect({
				componentType,
				sections: [
					{
						id: "fonts",
						title: "Font Styles",
						properties: ["statsValueFont", "statsLabelFont"],
					},
					{
						id: "labels",
						title: "Labels",
						properties: ["statsLabels"],
					},
					{
						id: "tabs",
						title: "Tab Configuration",
						properties: ["tabs", "tabsSharedStyles"],
					},
				],
				properties: {
					statsValueFont: {
						store: "design",
						type: "font",
						label: "Stats Value Font",
						description: "Font styling for the statistics values",
					},
					statsLabelFont: {
						store: "design",
						type: "font",
						label: "Stats Label Font",
						description: "Font styling for the statistics labels",
					},
					statsLabels: {
						store: "content",
						type: "group",
						label: "Stats Labels",
						description: "Labels for the statistics sections",
						properties: {
							posts: {
								type: "string",
								label: "Posts Label",
								description: "Label for the posts section",
							},
							followers: {
								type: "string",
								label: "Followers Label",
								description: "Label for the followers section",
							},
							following: {
								type: "string",
								label: "Following Label",
								description: "Label for the following section",
							},
						},
					},
					tabs: {
						store: "content",
						type: "tabitems",
						label: "Tab Items",
						description: "Items for the tabs",
						properties: {
							tabitems: {
								type: "tabitems",
								label: "Tabs",
								description: "Tabs for the profile header",
							},
						},
					},
					tabsSharedStyles: {
						store: "design",
						type: "tabs",
						label: "Tabs Styling",
						description: "Shared styling for all tabs",
						properties: {
							showIcon: {
								type: "boolean",
								label: "Show Icons",
								description: "Toggle visibility of tab icons",
							},
							showLabel: {
								type: "boolean",
								label: "Show Labels",
								description: "Toggle visibility of tab labels",
							},
							backgroundColor: {
								type: "color",
								label: "Background Color",
								description: "Background color of the tabs",
							},
							font: {
								type: "font",
								label: "Tab Font",
								description: "Font styling for tab labels",
							},
						},
					},
				},
			});
		}
	};

	const name = "Peta Parka";
	const bio = "I'm a software engineer and a startup founder. I'm also a big fan of the Yankees and the Knicks.";
	const avatar = "https://i.pravatar.cc/300";

	const formatNumber = (num) => {
		if (num >= 1000000) {
			return (num / 1000000).toFixed(1) + "M";
		}
		if (num >= 1000) {
			return (num / 1000).toFixed(1) + "K";
		}
		return num.toString();
	};

	return (
		<TouchableOpacity activeOpacity={1} onPress={handlePress}>
			<View style={styles(theme).container}>
				<View style={styles(theme).header}>
					<View style={styles(theme).topSection}>
						<View style={styles(theme).avatarContainer}>
							<Image source={{ uri: avatar }} style={styles(theme).avatar} />
						</View>
						<View style={styles(theme).statsContainer}>
							<View style={styles(theme).statItem}>
								<Text style={design.statsValueFont}>{formatNumber(12)}</Text>
								<Text style={design.statsLabelFont}>{content.statsLabels.posts || "Posts"}</Text>
							</View>
							<View style={styles(theme).statItem}>
								<Text style={design.statsValueFont}>{formatNumber(340)}</Text>
								<Text style={design.statsLabelFont}>{content.statsLabels.followers || "Followers"}</Text>
							</View>
							<View style={styles(theme).statItem}>
								<Text style={design.statsValueFont}>{formatNumber(567)}</Text>
								<Text style={design.statsLabelFont}>{content.statsLabels.following || "Following"}</Text>
							</View>
						</View>
					</View>

					<View style={styles(theme).profileInfo}>
						<Text style={styles(theme).name}>{name}</Text>
						<Text style={styles(theme).bio}>{bio}</Text>

						<View style={styles(theme).metaInfo}>
							<View style={styles(theme).metaItem}>
								<Ionicons name="location-outline" size={12} color={theme.colors.textSecondary} />
								<Text style={styles(theme).metaText}>{"Atlanta, GA"}</Text>
							</View>
						</View>

						<TouchableOpacity style={styles(theme).editButton}>
							<Text style={styles(theme).editButtonText}>Edit Profile</Text>
						</TouchableOpacity>
					</View>
				</View>

				<View style={styles(theme).tabsContainer}>
					{content.tabs.map((tab) => (
						<TouchableOpacity
							key={tab.id}
							onPress={() => handleTabPress(tab.id)}
							style={[
								styles(theme).tab,
								{
									backgroundColor: design.tabsSharedStyles.backgroundColor || theme.colors.systemBackground.primary,
									borderBottomWidth: 2,
									borderBottomColor: activeTab === tab.id ? theme.colors.primary : "transparent",
								},
							]}>
							{design.tabsSharedStyles.showIcon && <FontAwesome6 name={tab.icon} size={20} color={activeTab === tab.id ? theme.colors.primary : design.tabsSharedStyles.color || theme.colors.text} />}
							{design.tabsSharedStyles.showLabel && (
								<Text
									style={{
										fontFamily: design.tabsSharedStyles.fontFamily || "System",
										fontSize: design.tabsSharedStyles.fontSize || 14,
										fontWeight: activeTab === tab.id ? "600" : design.tabsSharedStyles.fontWeight || "400",
										color: activeTab === tab.id ? theme.colors.primary : design.tabsSharedStyles.color || theme.colors.text,
										marginLeft: design.tabsSharedStyles.showIcon ? 5 : 0,
									}}>
									{tab.label}
								</Text>
							)}
						</TouchableOpacity>
					))}
				</View>
			</View>
		</TouchableOpacity>
	);
};

const styles = (theme) =>
	StyleSheet.create({
		container: {
			backgroundColor: theme.colors.systemBackground.primary,
			paddingTop: theme.spacing.sm,
		},
		header: {
			paddingHorizontal: theme.spacing.lg,
		},
		topSection: {
			flexDirection: "row",
			alignItems: "center",
			marginBottom: theme.spacing.md,
		},
		avatarContainer: {
			marginRight: theme.spacing.lg,
		},
		avatar: {
			width: 77,
			height: 77,
			borderRadius: 38.5,
			borderWidth: 2,
			borderColor: theme.colors.primary,
		},
		statsContainer: {
			flex: 1,
			flexDirection: "row",
			justifyContent: "space-around",
		},
		statItem: {
			alignItems: "center",
		},
		profileInfo: {
			marginBottom: theme.spacing.xs,
		},
		name: {
			...theme.typography.subheadline,
			color: theme.colors.text,
			fontWeight: "600",
			marginBottom: theme.spacing.xxs,
		},
		bio: {
			...theme.typography.caption1,
			color: theme.colors.text,
			marginBottom: theme.spacing.xs,
		},
		editButton: {
			backgroundColor: theme.colors.gray[6],
			paddingVertical: theme.spacing.sm,
			paddingHorizontal: theme.spacing.lg,
			borderRadius: theme.borderRadius.lg,
			alignItems: "center",
			marginTop: theme.spacing.xs,
		},
		editButtonText: {
			...theme.typography.caption1,
			color: theme.colors.text,
			fontWeight: "500",
		},
		metaInfo: {
			flexDirection: "row",
			alignItems: "center",
			marginBottom: theme.spacing.xs,
		},
		metaItem: {
			flexDirection: "row",
			alignItems: "center",
		},
		metaText: {
			...theme.typography.caption2,
			color: theme.colors.textSecondary,
			marginLeft: theme.spacing.xxs,
		},
		tabsContainer: {
			flexDirection: "row",
			justifyContent: "space-around",
			borderBottomWidth: 1,
			borderBottomColor: theme.colors.gray[6],
			marginTop: theme.spacing.xs,
		},
		tab: {
			flexDirection: "row",
			alignItems: "center",
			paddingVertical: 12,
			paddingHorizontal: 16,
			gap: theme.spacing.sm,
		},
	});

// Wrapper component to provide context and theme
const ProfileHeaderWrapper = (props) => {
	const theme = useTheme();
	const pageContext = useProfilePage();
	const WrappedProfileHeader = withComponentEditor(ProfileHeader);

	return <WrappedProfileHeader {...props} theme={theme} pageContext={pageContext} />;
};

export default ProfileHeaderWrapper;
